// Top-level routing.
//
// **Routes:**
//   - `/login` → `LoginRedirect`. Public.
//   - `/logout` → `LogoutRedirect`. Public.
//   - `/` and all other authenticated routes → wrapped by
//     `AuthGate` which: (1) probes `trpc.whoAmI` once, (2) on 401
//     redirects to `/login`, (3) on success renders `AppLayout`.
//   - Domain views (`/agents`, `/runs`, `/approvals`, `/settings`)
//     render empty placeholders at fn-1.28; fn-1.29 fills them in.
//
// **Why a single shared `AuthGate` wrapper** rather than per-route
// `loader` functions:
//   - React Router v6 loaders can do auth probes, but the loader runs
//     BEFORE the QueryClient is available to other consumers. A
//     single component-level gate keeps the auth probe inside the
//     React tree so it shares the QueryClient cache with the rest of
//     the app.
//   - Loaders also force `tRPC` to be called from a non-hook context,
//     which means the vanilla client. We have one already
//     (`createVanillaTrpcClient`) but doubling the surface area
//     isn't worth it at v1.

import { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { bffEndpoints } from './auth/bff-origin.js';
import { LoginRedirect } from './auth/login.js';
import { LogoutRedirect } from './auth/logout.js';
import { ClientOtelProvider } from './instrumentation/provider.js';
import { AppLayout } from './layout/app-layout.js';
import { AgentEditPage, AgentNewPage } from './pages/agents/agent-form.js';
import { AgentDetailPage } from './pages/agents/detail.js';
import { AgentsListPage } from './pages/agents/list.js';
import { AgentTryPage } from './pages/agents/try.js';
import { ApprovalsListPage } from './pages/approvals/list.js';
import { ArchieHeadlessPage } from './pages/archie-headless/index.js';
import { ConnectorsListPage } from './pages/connectors/list.js';
import { FilesListPage } from './pages/files/list.js';
import { GatewayToolsListPage } from './pages/gateway-tools/list.js';
import { InboxSessionPage } from './pages/inbox/detail.js';
import { InboxPage } from './pages/inbox/index.js';
import { McpServersListPage } from './pages/mcp-servers/list.js';
import { NetworkProfilesListPage } from './pages/network-profiles/list.js';
import {
  PreviewChatPage,
  PreviewMatterChatStartPage,
  PreviewPage,
  PreviewStartPage,
} from './pages/preview/index.js';
import { RunDetailPage } from './pages/runs/detail.js';
import { RunsListPage } from './pages/runs/list.js';
import { SkillsListPage } from './pages/skills/list.js';
import { trpc } from './trpc-client.js';

/**
 * The auth-gate decision for an error httpStatus (fn-46.3 / R8). Pure +
 * exported so the 401-vs-403 contract is unit-testable without mounting
 * the tRPC tree:
 *   - `401` → `'login'` (session missing/expired — redirect to /login).
 *   - `403` → `'forbidden'` (authenticated but NOT authorized — render a
 *     terminal no-access state; NEVER redirect to login, which would loop).
 *   - anything else (incl. `undefined` network error) → `'error'`
 *     (transient notice + retry).
 */
export type AuthGateAction = 'login' | 'forbidden' | 'error';
export function authGateActionForStatus(status: number | undefined): AuthGateAction {
  if (status === 401) return 'login';
  if (status === 403) return 'forbidden';
  return 'error';
}

/**
 * Auth gate. Probes `trpc.whoAmI`; renders children on success,
 * redirects to `/login` on 401, renders a terminal no-access state on
 * 403 (authenticated but unauthorized — never a login redirect, which
 * would loop), and a spinner while loading.
 *
 * **Return-to handling (codex impl-review round 1 fix):** when we
 * `<Navigate to="/login" />`, we capture the CURRENT location into
 * router state under `state.from`. The `/login` route reads it and
 * passes it to `LoginRedirect` so the BFF's `return_to` parameter
 * matches the user's original deep link — not `/login` itself.
 * Without this, a logged-out user deep-linking to `/runs/abc` would
 * bounce to `/login`, Cognito would complete, the BFF would redirect
 * back to `/login`, and the SPA would loop back to login.
 */
function AuthGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const location = useLocation();
  const query = trpc.whoAmI.useQuery(undefined, {
    // Don't auto-retry — 401 is the expected unauthenticated state.
    retry: false,
    // Keep the result fresh across route navigations. The hook is
    // mounted by `AuthGate` which wraps every authenticated route;
    // a stable cache means the probe runs once per browser session
    // (until a 401 invalidates it).
    staleTime: 5 * 60 * 1000,
  });

  if (query.isPending) {
    return (
      <main className="auth-pending" aria-busy="true">
        <p>Loading…</p>
      </main>
    );
  }
  if (query.error !== null) {
    // tRPC v11 attaches a `data.httpStatus` for httpLink errors; we
    // map 401 → login redirect. Any other error → display a brief
    // notice and let the user retry. We treat undefined httpStatus
    // (network error) as "transient" and surface as a retry; the
    // user manually navigates to `/login` if they want.
    const status = (query.error.data as { httpStatus?: number } | null | undefined)?.httpStatus;
    const action = authGateActionForStatus(status);
    if (action === 'login') {
      // Carry the requested URL through router state so the login
      // page can rebuild it as the BFF `return_to`. We carry path +
      // search + hash, NOT the origin — the BFF wires the origin
      // from its config (open-redirect protection).
      const from = `${location.pathname}${location.search}${location.hash}`;
      return <Navigate to="/login" replace state={{ from }} />;
    }
    // fn-46.3 (R8) — 403 is "authenticated but not authorized" (missing
    // scope / role / actor-type at the CP or BFF). It must NEVER redirect
    // to `/login`: the credential is valid, so a re-login would land back
    // here and loop. Render a terminal no-access state instead. Only 401
    // (session missing/expired) routes to login.
    if (action === 'forbidden') {
      return (
        <main className="auth-forbidden" role="alert">
          <h1>No access</h1>
          <p>
            Your account is signed in but does not have access to this area. Contact your
            administrator if you believe this is a mistake.
          </p>
        </main>
      );
    }
    return (
      <main className="auth-error" role="alert">
        <p>Unable to confirm sign-in. Try refreshing.</p>
      </main>
    );
  }
  // fn-1.31 — mount the SPA client-otel provider once authenticated.
  // The `userId` from `whoAmI` is the SPA-side session correlator;
  // the BFF mirrors this in its own span (`bff.<procedure>`), so all
  // three tiers (SPA / BFF / control-plane) share the same Langfuse
  // trace id (`hash(userId)` for the SPA + BFF + per-run platform
  // session id for the run-specific spans).
  return <ClientOtelProvider userId={query.data.userId}>{children}</ClientOtelProvider>;
}

/**
 * `LocationAwareLoginRedirect` — wraps `LoginRedirect` with router
 * `useLocation` so it can read `state.from` (set by `AuthGate`) and
 * pass it as the explicit `returnTo` to `LoginRedirect`. Without
 * this wrapper, the SPA's `LoginRedirect` would default to
 * `window.location.href` which IS the `/login` URL — defeating the
 * deep-link capture.
 *
 * **Why a wrapper, not a hook inside `LoginRedirect`:** the auth
 * components live in a router-agnostic module (no `react-router`
 * imports) so they remain unit-testable without a Router context.
 * The wrapper does the router-coupling here.
 *
 * **`return_to` is RELATIVE (fn-45.4 codex round 2 fix):** the BFF's
 * `isSafeReturnPath` accepts only same-origin paths starting with `/`
 * (open-redirect protection). Sending the ABSOLUTE `window.location.href`
 * made the BFF reject every deep link and fall back to `/`. We send only
 * the path+search+hash and let the BFF reconstruct against its own
 * configured origin on the post-login redirect.
 *
 * **`bffLoginPath` is ABSOLUTE in deployed mode (fn-45.4 cross-origin
 * fix):** the login endpoint lives on the BFF origin (`VITE_BFF_URL`),
 * not the SPA origin — a relative `/auth/login` would 404 on the SPA.
 * Login is a top-level browser navigation, so the browser attaches the
 * BFF-origin cookies itself.
 */
function LocationAwareLoginRedirect(): React.ReactElement {
  const location = useLocation();
  const state = location.state as { from?: unknown } | null;
  const from = typeof state?.from === 'string' ? state.from : undefined;
  // The RELATIVE return path (path+search+hash, NO origin). When `from`
  // is missing (direct navigation to `/login`), default to `/`. The BFF
  // wires the origin from its own config (open-redirect protection), so
  // it only ever accepts — and round-trips — a relative path.
  const returnTo = safeLoginReturnPath(from);
  return <LoginRedirect returnTo={returnTo} bffLoginPath={bffEndpoints().loginUrl} />;
}

function safeLoginReturnPath(from: string | undefined): string {
  if (from === undefined) return '/';
  if (!from.startsWith('/') || from.startsWith('//')) return '/';
  if (from.includes('://') || from.includes('\\')) return '/';
  if (from === '/login' || from.startsWith('/login?') || from.startsWith('/login#')) return '/';
  return from;
}

/**
 * The router. Exported so tests can mount it under a `MemoryRouter`
 * (and `main.tsx` mounts it under `BrowserRouter`).
 */
export function AppRoutes(): React.ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LocationAwareLoginRedirect />} />
      <Route path="/logout" element={<LogoutRedirect bffLogoutPath={bffEndpoints().logoutUrl} />} />
      <Route
        path="/"
        element={
          <AuthGate>
            <AppLayout />
          </AuthGate>
        }
      >
        <Route index element={<Navigate to="/agents" replace />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="inbox/:id" element={<InboxSessionPage />} />
        <Route path="agents" element={<AgentsListPage />} />
        {/* fn-71 / ADR 0035 — admin-only DB-backed agent authoring. The static
            `new` segment outranks `:id` regardless of order (like `preview/new`);
            the per-page admin gate hides the form from non-admins. */}
        <Route path="agents/new" element={<AgentNewPage />} />
        <Route path="agents/:id/edit" element={<AgentEditPage />} />
        {/* fn-71 / ADR 0035 / R12 — "Try it": a matter-less test session +
            ui-react Chat preview of a saved agent. Static `try` segment outranks
            `:id`; the page is admin-gated. */}
        <Route path="agents/:id/try" element={<AgentTryPage />} />
        <Route path="agents/:id" element={<AgentDetailPage />} />
        <Route path="skills" element={<SkillsListPage />} />
        <Route path="mcp-servers" element={<McpServersListPage />} />
        <Route path="network-profiles" element={<NetworkProfilesListPage />} />
        <Route path="gateway-tools" element={<GatewayToolsListPage />} />
        {/* fn-72.4 — preview-only merged view of MCP servers + gateway tools.
            Additive: the standalone pages above stay. Nav link is preview-gated
            (cosmetic); the route stays registered + reachable by URL. */}
        <Route path="connectors" element={<ConnectorsListPage />} />
        <Route path="files" element={<FilesListPage />} />
        <Route path="runs" element={<RunsListPage />} />
        <Route path="runs/:id" element={<RunDetailPage />} />
        <Route path="approvals" element={<ApprovalsListPage />} />
        {/* fn-48.5 — staff testbed for the end-user components. The
            static `new` segment outranks `:id` regardless of order. */}
        <Route path="preview" element={<PreviewPage />} />
        <Route path="preview/new" element={<PreviewStartPage />} />
        <Route path="preview/matter-chat" element={<PreviewMatterChatStartPage />} />
        <Route path="preview/:id" element={<PreviewChatPage />} />
        {/* fn-69.3 — ArchIE headless matter-intake demo (status strip,
            not a chat transcript). Self-contained; reuses the fn-69.2
            driver over the BFF, no shared `<Chat>`/reducer changes. */}
        <Route path="archie" element={<ArchieHeadlessPage />} />
        {/* fn-72.2 — the empty Settings placeholder (route + page) was removed;
            `/settings` now falls through to NotFound. */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function NotFound(): React.ReactElement {
  return (
    <section className="not-found" role="alert">
      <h1>Not found</h1>
      <p>The page you requested doesn’t exist.</p>
    </section>
  );
}

/**
 * Top-level App component. `main.tsx` wraps this with the tRPC +
 * react-query providers.
 */
export function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <Suspense fallback={<p>Loading…</p>}>
        <AppRoutes />
      </Suspense>
    </BrowserRouter>
  );
}
